import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Spinner } from '@/components/ui/Spinner';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { useTourDetails, usePurchaseTour } from '@/api/queries/tour';
import { useWallet } from '@/api/queries/wallet';
import { centsToUsdString } from '@/utils/money';
import { useLocale } from '@/hooks/useLocale';
import { useUIStore } from '@/store/ui';
import { isAPIError } from '@/api/error';
import { paths } from '@/routes/paths';

export function TravelDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { id = '' } = useParams<{ id: string }>();
  const tour = useTourDetails(id);
  const wallet = useWallet();
  const buy = usePurchaseTour();
  const pushToast = useUIStore((s) => s.pushToast);
  const navigate = useNavigate();

  const sufficient =
    (wallet.data?.available_cents ?? 0) >= (tour.data?.min_wallet_balance_required_cents ?? 0);

  const handleBuy = async (payWith: 'wallet' | 'card') => {
    try {
      const r = await buy.mutateAsync({ id, payWith });
      if (r.kind === 'intent' && r.intent.checkout_url) {
        // Card-flow: redirect user to external checkout (in-app browser stub).
        window.open(r.intent.checkout_url, '_blank', 'noopener');
        pushToast({ body: t('travel.tour.checkout_opened'), variant: 'info' });
      } else if (r.kind === 'purchase') {
        // Wallet-funded: instant success.
        pushToast({ body: t('travel.tour.purchase_success'), variant: 'success' });
        navigate(paths.travel);
      } else {
        // Defensive fallback (intent without checkout_url).
        pushToast({ body: t('errors.generic'), variant: 'error' });
      }
    } catch (err) {
      const key = isAPIError(err) ? err.i18nKey : 'errors.generic';
      pushToast({ body: t(key, { defaultValue: t('errors.generic') }), variant: 'error' });
    }
  };

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={tour.data?.title ?? ''} />
        {tour.isLoading && <Spinner />}
        {tour.data && (
          <>
            {tour.data.cover_image_url && (
              <img
                src={tour.data.cover_image_url}
                alt=""
                style={{ borderRadius: 16, width: '100%', objectFit: 'cover' }}
              />
            )}
            <Card variant="light" padding="md">
              <h1 style={{ margin: 0 }}>{tour.data.title}</h1>
              {tour.data.city_name && (
                <div style={{ color: 'var(--color-text-on-light-muted)', marginTop: 6 }}>
                  {tour.data.city_name}, {tour.data.country_code}
                </div>
              )}
              <p style={{ marginBlock: 12 }}>{tour.data.description}</p>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <strong style={{ fontSize: 22 }}>
                  {centsToUsdString(tour.data.from_price_cents, locale)}
                </strong>
                <span style={{ color: 'var(--color-text-on-light-muted)' }}>
                  {tour.data.per_unit === 'per_night'
                    ? t('travel.tour.per_night')
                    : tour.data.per_unit === 'per_person'
                      ? t('travel.tour.per_person')
                      : t('travel.tour.total')}
                </span>
              </div>
            </Card>
            {!sufficient && tour.data.min_wallet_balance_required_cents > 0 && (
              <Banner variant="warning">
                {t('travel.tour.min_balance_required', {
                  amount: centsToUsdString(tour.data.min_wallet_balance_required_cents, locale),
                })}
              </Banner>
            )}
            <Button block size="lg" variant="primary" disabled={!sufficient} onClick={() => handleBuy('wallet')} loading={buy.isPending}>
              {t('travel.tour.buy')}
            </Button>
            <Button block variant="secondary" onClick={() => handleBuy('card')} loading={buy.isPending}>
              {t('travel.tour.buy_with_card')}
            </Button>
          </>
        )}
      </PageContainer>
    </AppShell>
  );
}
